import React from "react";
import produce from "immer";
import { useDispatch } from "react-redux";
import { Box, Button, Typography } from "@mui/material";

import { AsyncAction } from "../../../../actions/actionHelpers";
import ImageCrop from "../../../../components/ImageCrop/ImageCrop";
import { notificationAdd } from "../../../../actions/notification";
import { customFooterImagePresignedS3 } from "../../../../api/resourceCentre";
import { LabSettings, ResourceCentre } from "../../../../interfaces/ResourceCentreInterface";
import CustomFooterImage from "../../../Lab/LabPrint/LabprintComponents/CustomFooterImage";

const FooterImageSettings = ({
  labSettings,
  resourceCentre,
  actions
}: {
  labSettings: LabSettings;
  resourceCentre: ResourceCentre;
  actions: {
    [x: string]: (options?: Record<string, unknown>, next?: unknown) => AsyncAction<unknown>;
  };
}): JSX.Element => {
  const dispatch = useDispatch();
  const fileUploaderRef = React.useRef<HTMLInputElement>(null);
  const [tempImg, setTempImg] = React.useState<string | null>(null);
  const [isCropperOpen, setIsCropperOpen] = React.useState(false);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e?.target?.files?.length) {
      return;
    }
    if (e?.target?.files[0]?.size >= 200000) {
      // show error dialog if file size exceeds 200KB
      // eslint-disable-next-line no-param-reassign
      e.target.value = "";
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "File size needs to be less than 200KB.",
          autoTimeout: true
        })
      );
      return;
    }
    const file = e?.target?.files[0];
    const extensionIndex = file?.name?.lastIndexOf(".") + 1;
    const extension = file?.name?.substr(extensionIndex)?.toLowerCase();
    if (extension === "jpg" || extension === "jpeg" || extension === "png" || extension === "gif") {
      setTempImg(URL.createObjectURL(file));
      setIsCropperOpen(true);
    } else {
      // eslint-disable-next-line no-param-reassign
      e.target.value = "";
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "Invalid image type.",
          autoTimeout: true
        })
      );
    }
  };

  return (
    <Box mt={2}>
      <Typography sx={{ fontWeight: 600 }} gutterBottom>
        Custom Print Footer (772px * 65px)
      </Typography>
      <Box>
        {labSettings.customFooterImage && (
          <CustomFooterImage showPoweredByLogo={false} imageUrl={labSettings.customFooterImage} />
        )}
      </Box>
      <label htmlFor="footerFileUploader">
        <input
          className="imageUpload"
          data-testmation="uploadFooterBannerInput"
          type="file"
          accept=".jpg,.jpeg,.gif,.png"
          ref={fileUploaderRef}
          name="footerFileUploader"
          onChange={handleImageChange}
          id="footerFileUploader"
          style={{
            display: "none"
          }}
        />
        <Button variant="contained" component="span">
          Upload Custom Footer
        </Button>
      </label>
      {labSettings.customFooterImage && (
        <Button
          variant="contained"
          sx={{ backgroundColor: "red", marginLeft: 1 }}
          onClick={() => {
            const newRc = produce(resourceCentre, (draft) => {
              draft.labSettings.customFooterImage = "";
            });
            dispatch(actions.putResourceCentre(newRc));
          }}
        >
          Remove Footer Image
        </Button>
      )}
      <ImageCrop
        image={tempImg}
        dynamicRatio={false}
        defaultRatio={{ x: 12, y: 1 }}
        isOpen={isCropperOpen}
        onClose={() => setIsCropperOpen(false)}
        onSave={async (img) => {
          try {
            const data = await customFooterImagePresignedS3(img);
            if (!data) {
              return;
            }
            const newRc = produce(resourceCentre, (draft) => {
              draft.labSettings.customFooterImage = data;
            });
            dispatch(actions.putResourceCentre(newRc));
          } catch (err) {
            dispatch(
              notificationAdd({
                id: new Date().getUTCMilliseconds(),
                variant: "error",
                message: "Failed to upload custom footer",
                autoTimeout: true
              })
            );
          }
        }}
      />
    </Box>
  );
};

export default FooterImageSettings;
